import {
  BookOpen,
  Check,
  GraduationCap,
  School,
  ShieldCheck,
} from "lucide-react";
import { Modal } from "@/shared/components/Modal";
import { cn } from "@/shared/lib/utils";
import { roleLabel } from "@/shared/lib/role-label";
import { useContextSwitch } from "../hooks/useContextSwitch";

/** Shared school and portal choices keep normal navigation and access-denied recovery on one session policy. */
export function ContextSwitchModal({
  kind,
  onDismiss,
}: {
  kind: "school" | "portal";
  onDismiss: () => void;
}) {
  const context = useContextSwitch(onDismiss);
  const choices =
    kind === "school"
      ? context.schools.map((school) => ({
          id: school.tenantId,
          title: school.tenantName,
          description: school.roles
            .map((role) => roleLabel(role, school.adminTitle))
            .join(" · "),
          selected: school.tenantId === context.activeTenant?.tenantId,
          Icon: School,
        }))
      : context.portals.map((portal) => ({
          id: portal.role,
          title: portal.title,
          description: portal.description,
          selected: portal.role === context.currentPortal?.role,
          Icon:
            portal.role === "Admin"
              ? ShieldCheck
              : portal.role === "Teacher"
                ? GraduationCap
                : BookOpen,
        }));
  return (
    <Modal
      id={`context-switch-${kind}`}
      title={kind === "school" ? "تغییر مدرسه فعال" : "تغییر پرتال"}
      onDismiss={onDismiss}
      initialFocusSelector={
        choices.some((choice) => choice.selected)
          ? "[data-context-selected]"
          : "[data-context-choice]"
      }
    >
      <p className="mb-4 text-xs leading-6 text-slate-500">
        {kind === "school"
          ? "مدرسه‌ای را که می‌خواهید در آن کار کنید انتخاب کنید."
          : "پرتال مورد نظر را در مدرسه فعال انتخاب کنید."}
      </p>
      <ul className="space-y-2">
        {choices.map(({ id, title, description, selected, Icon }) => (
          <li key={id}>
            <button
              type="button"
              data-context-choice
              data-context-selected={selected ? "" : undefined}
              aria-pressed={selected}
              onClick={() =>
                kind === "school"
                  ? context.selectSchool(id)
                  : context.selectPortal(id)
              }
              className={cn(
                "focus-visible:outline-brand flex min-h-16 w-full items-start gap-3 rounded-2xl border p-3 text-start transition-colors focus-visible:outline-2 focus-visible:outline-offset-2",
                selected
                  ? "border-violet-200 bg-violet-50/70"
                  : "border-slate-200 bg-white hover:border-violet-200 hover:bg-violet-50/40",
              )}
            >
              <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-600">
                <Icon className="size-5" aria-hidden="true" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm leading-6 font-bold [overflow-wrap:anywhere] text-slate-800">
                  {title}
                </span>
                {description && (
                  <span className="mt-0.5 block text-[11px] leading-5 [overflow-wrap:anywhere] text-slate-500">
                    {description}
                  </span>
                )}
                {selected && (
                  <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-violet-700">
                    <Check className="size-3" aria-hidden="true" />
                    فعال
                  </span>
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {choices.length === 0 && (
        <p role="status" className="py-3 text-center text-xs text-slate-500">
          گزینه‌ای در دسترس نیست.
        </p>
      )}
    </Modal>
  );
}
